import { useRef, useState } from 'react';

import {
  Button,
  Heading,
  Icon,
  Input,
  LoadingIcon,
  Stack,
  Text,
  cn,
  useToast,
} from '@eluve/components';
import { MAX_PDF_UPLOAD_BYTES } from '@eluve/utils';

import {
  type ReviewField,
  ReviewFillableFieldsView,
} from './ReviewFillableFieldsView';
import { TemplateSavedView } from './TemplateSavedView';

export type PdfGenerateResult = {
  generatedFileId: string;
  fields: ReviewField[];
  alreadyFillable: boolean;
};

export type PdfTemplateCreateFlowProps = {
  onBack: () => void;
  onClose: () => void;
  onFillPdfForm: (templateId: string, name: string) => void;
  uploadFile: (file: File) => Promise<string>;
  generate: (userUploadId: string) => Promise<PdfGenerateResult>;
  save: (
    sourceFileId: string,
    name: string,
  ) => Promise<{ templateId: string; name: string }>;
  usePreviewUrl: (fileId: string | null) => string | null;
};

type Status = 'upload' | 'generating' | 'review' | 'saving' | 'saved' | 'error';

export const PdfTemplateCreateFlow: React.FC<PdfTemplateCreateFlowProps> = ({
  onBack,
  onClose,
  onFillPdfForm,
  uploadFile,
  generate,
  save,
  usePreviewUrl,
}) => {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [status, setStatus] = useState<Status>('upload');
  const [errorMessage, setErrorMessage] = useState('');
  const [result, setResult] = useState<PdfGenerateResult | null>(null);
  const [templateId, setTemplateId] = useState('');
  const [name, setName] = useState('');
  const [fileName, setFileName] = useState('');
  const [isDragging, setIsDragging] = useState(false);

  const previewUrl = usePreviewUrl(result?.generatedFileId ?? null);

  const handleFile = async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are allowed');
      return;
    }
    if (file.size > MAX_PDF_UPLOAD_BYTES) {
      toast.error('File size must be less than 10 MB');
      return;
    }

    setFileName(file.name);
    setStatus('generating');
    try {
      const id = await uploadFile(file);
      const r = await generate(id);
      setResult(r);
      setStatus('review');
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : 'Something went wrong');
      setStatus('error');
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (inputRef.current) inputRef.current.value = '';
    if (file) void handleFile(file);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) void handleFile(file);
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    if (event.dataTransfer.types.includes('Files')) setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const handleSave = async () => {
    if (!result) return;
    setStatus('saving');
    try {
      const t = await save(result.generatedFileId, name.trim());
      setTemplateId(t.templateId);
      // Adopt the server's canonical name so the saved view + fill callback
      // match what was persisted (not the untrimmed input).
      setName(t.name);
      setStatus('saved');
    } catch {
      toast.error('Failed to save template');
      setStatus('review');
    }
  };

  if (status === 'review' || status === 'saving') {
    return (
      <ReviewFillableFieldsView
        fileName={fileName}
        fields={result?.fields ?? []}
        alreadyFillable={result?.alreadyFillable ?? false}
        previewUrl={previewUrl}
        name={name}
        onNameChange={setName}
        onCancel={onClose}
        onSave={handleSave}
        isSaving={status === 'saving'}
      />
    );
  }

  if (status === 'saved') {
    return (
      <TemplateSavedView
        name={name}
        onFillFromNote={() => onFillPdfForm(templateId, name)}
        onDone={onClose}
      />
    );
  }

  if (status === 'generating') {
    return (
      <Stack align="center" justify="center" gap={4} className="p-10">
        <LoadingIcon size="xl" className="text-text-indigo" />
        <Stack gap={1} align="center">
          <Stack
            direction="horizontal"
            gap={1}
            align="center"
            justify="center"
            fullWidth={false}
          >
            <Icon name="Sparkles" size="sm" className="text-text-indigo" />
            <Text size="sm" weight="medium">
              Eluve is reading the layout and adding form fields…
            </Text>
          </Stack>
          <Text size="xs" color="tertiary" className="text-center">
            Detecting labels, checkboxes and signature areas
          </Text>
        </Stack>
      </Stack>
    );
  }

  if (status === 'error') {
    return (
      <Stack align="center" justify="center" gap={4} className="p-10">
        <Text size="sm" className="text-text-negative">
          {errorMessage}
        </Text>
        <Button variant="outline" onClick={() => setStatus('upload')}>
          Try another file
        </Button>
      </Stack>
    );
  }

  return (
    <Stack gap={6}>
      <Button
        variant="ghost"
        size="sm"
        onClick={onBack}
        className="-ml-1 self-start text-text-secondary"
      >
        <Icon name="ArrowLeft" size="auto" />
        Back
      </Button>
      <Stack gap={1} className="pr-8">
        <Heading size="lg">Create a PDF form template</Heading>
        <Text size="xs" color="tertiary">
          Upload a static PDF. Eluve checks for fillable fields and adds them
          with AI if needed.
        </Text>
      </Stack>
      <Input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={handleInputChange}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={cn(
          'flex w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-6 py-12 text-center transition-colors',
          isDragging
            ? 'border-border-indigo-strong bg-bg-indigo-subtle-primary/30'
            : 'border-border-primary hover:bg-bg-tertiary',
        )}
      >
        <Icon name="Upload" size="xl" className="text-text-tertiary" />
        <Text size="sm" weight="medium">
          {isDragging
            ? 'Drop your PDF here'
            : 'Click to browse, or drag and drop your PDF here'}
        </Text>
        <Text size="xs" color="tertiary">
          PDF only, max 10 MB
        </Text>
      </button>
    </Stack>
  );
};
